import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/service/auth-service/auth.service';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { urls } from 'src/app/utils/url-utils';

// Angular route segment -> React planner route, per the shared page/path map.
const REACT_PATH_BY_SEGMENT: { [segment: string]: string } = {
  trips: '/',
  bucket: '/bucket',
  maps: '/maps',
  'portal-plan': '/portal-plan',
  charts: '/charts'
};

@Component({
  selector: 'app-planner-v2',
  templateUrl: './planner-v2.component.html',
  styleUrls: ['./planner-v2.component.css']
})
export class PlannerV2Component implements OnInit, OnDestroy {
  @ViewChild('plannerIframe', { static: false }) plannerIframe!: ElementRef<HTMLIFrameElement>;

  portalUrl: SafeResourceUrl = '';
  isIframeLoading = true;
  private readonly reactOrigin = new URL(urls.PLANNER_BASE_URL).origin;
  private paramMapSubscription?: Subscription;
  private isPortalLoaded = false;

  constructor(
    private authService: AuthService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private sharedDataService: SharedDataService
  ) { }

  ngOnInit() {
    // Route reuses the same component instance across planner/v2/:route
    // navigations, so param changes must be listened to rather than
    // relying only on the initial snapshot.
    this.paramMapSubscription = this.route.paramMap.subscribe(params => {
      const urlSegment = params.get('route') || '';

      if (!this.isPortalLoaded) {
        this.loadPortal(urlSegment);
      } else {
        this.navigatePortal(urlSegment);
      }
    });
  }

  ngOnDestroy() {
    if (this.paramMapSubscription) {
      this.paramMapSubscription.unsubscribe();
    }
  }

  private loadPortal(urlSegment: string) {
    let url: string;
    if (this.sharedDataService.isPlannerPortalInitialized()) {
      url = `${urls.PLANNER_BASE_URL}${urlSegment === 'trips' ? '' : urlSegment}`;
    } else {
      const token = this.authService.getToken('token');
      url = `${urls.PLANNER_BASE_URL}portal_oauth?token=JWT ${token}&env=${urls.IS_PROD ? 'live' : 'dev'}&base_url=${urls.SERVER_URL}api&isShowSidenav=false`;
      this.sharedDataService.setPlannerPortalInitialized();
    }

    this.portalUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.isPortalLoaded = true;
  }

  // Once loaded, the iframe src must never change again — page switches
  // are done via postMessage so the React planner navigates without a
  // hard refresh.
  private navigatePortal(urlSegment: string) {
    const path = REACT_PATH_BY_SEGMENT[urlSegment];
    if (!path) {
      return;
    }

    const contentWindow = this.plannerIframe && this.plannerIframe.nativeElement.contentWindow;
    if (!contentWindow) {
      return;
    }

    contentWindow.postMessage(
      {
        source: 'nemt-portal',
        type: 'NAVIGATE',
        path
      },
      this.reactOrigin
    );
  }
}
